import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { injectModalRef } from '../modal';
import { DocsSearchResult, DocsSearchService } from '../../core/services/docs-search.service';

/** Shown before the user types anything a fixed "browse" list, not a
 *  search result one entry per top-level guide/reference page,
 *  deliberately not every anchor DocsSearchService's own index also
 *  matches. */
const TOP_PAGES: DocsSearchResult[] = [
  { page: '', fragment: null, header: 'Introduction', content: 'The metered retrieval API for AI, built on the Cikisi index.', breadcrumbs: ['Get started', 'Introduction'], score: 0 },
  { page: 'quickstart', fragment: null, header: 'Quickstart', content: 'From nothing to a first response, in three steps.', breadcrumbs: ['Get started', 'Quickstart'], score: 0 },
  { page: 'authentication', fragment: null, header: 'Authentication', content: 'Every endpoint is a POST with a JSON body, authenticated with an organization API key issued to you from the developer portal.', breadcrumbs: ['Get started', 'Authentication'], score: 0 },
  { page: 'making-a-call', fragment: null, header: 'Making a call', content: 'The body is nested, not flat parameters live inside a request or response envelope.', breadcrumbs: ['Guides', 'Making a call'], score: 0 },
  { page: 'pricing-limits', fragment: null, header: 'Pricing & limits', content: 'Top up your wallet. Calls are authorized against the balance.', breadcrumbs: ['Guides', 'Pricing & limits'], score: 0 },
  { page: 'errors', fragment: null, header: 'Errors', content: 'Every error is the same envelope. Switch on code, never on the message text.', breadcrumbs: ['Guides', 'Errors'], score: 0 },
  { page: 'account-filters', fragment: null, header: 'Account-scoped filters', content: 'Everything else dates, domains, language, geography behaves the same either way.', breadcrumbs: ['Guides', 'Account-scoped filters'], score: 0 },
  { page: 'api-reference', fragment: null, header: 'All endpoints', content: 'Every endpoint the API exposes, with request/response schemas.', breadcrumbs: ['API Reference', 'All endpoints'], score: 0 },
  { page: 'api-reference/rag', fragment: null, header: 'RAG', content: 'Retrieval-augmented search over the Cikisi index: a natural-language question in, the passages that answer it out.', breadcrumbs: ['API Reference', 'RAG'], score: 0 },
  { page: 'api-reference/answer', fragment: null, header: 'ANSWER', content: 'Ask a question and get it answered in prose, written from the passages /rag would have returned.', breadcrumbs: ['API Reference', 'ANSWER'], score: 0 },
  { page: 'api-reference/search', fragment: null, header: 'SEARCH', content: 'Boolean search over the index, returning articles rather than passages.', breadcrumbs: ['API Reference', 'SEARCH'], score: 0 },
  { page: 'changelog', fragment: null, header: 'Changelog', content: "What's changed in the Mila Retrieval API.", breadcrumbs: ['Changelog'], score: 0 },
];

/** One consecutive run of highlighted text either a literal chunk or the
 *  part that matched the typed query (see matchParts()) rendered as
 *  separate spans so only the match gets highlighted. */
interface TextPart {
  text: string;
  hit: boolean;
}

@Component({
  selector: 'app-docs-search',
  standalone: false,
  templateUrl: './docs-search.html',
  styleUrl: './docs-search.scss',
})
export class DocsSearch implements AfterViewInit, OnDestroy {
  @ViewChild('input', { static: true }) inputRef!: ElementRef<HTMLInputElement>;

  query = '';
  results: DocsSearchResult[] = TOP_PAGES;
  activeIndex = 0;

  private readonly queryChange$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  readonly modalRef = injectModalRef<void>();

  constructor(
    private router: Router,
    private docsSearch: DocsSearchService,
  ) {
    // Debounced purely to smooth the results list for a fast typist, not
    // for any network/perf reason DocsSearchService.search() is a
    // synchronous local filter, no request in flight to cancel.
    this.queryChange$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap(q => {
        const trimmed = q.trim();
        return trimmed ? this.docsSearch.search(trimmed) : of(TOP_PAGES);
      }),
      takeUntil(this.destroy$),
    ).subscribe(results => {
      this.results = results;
      this.activeIndex = 0;
    });
  }

  ngAfterViewInit(): void {
    this.inputRef.nativeElement.focus();
  }

  onQueryChange(): void {
    this.queryChange$.next(this.query);
  }

  /** Splits `text` around the first case-insensitive match of the typed
   *  query, so the template can render the matched slice in its own
   *  highlighted span. Empty query, or no match in this particular
   *  field (the hit could be in the other one), returns it unsplit. */
  matchParts(text: string): TextPart[] {
    const q = this.query.trim();
    if (!q) return [{ text, hit: false }];
    const start = text.toLowerCase().indexOf(q.toLowerCase());
    if (start === -1) return [{ text, hit: false }];
    const end = start + q.length;
    return [
      { text: text.slice(0, start), hit: false },
      { text: text.slice(start, end), hit: true },
      { text: text.slice(end), hit: false },
    ].filter(part => part.text.length > 0);
  }

  /** Which glyph a result gets (see docs-search.html's @switch) derived
   *  from the data rather than hand-mapped per page, so it never needs
   *  updating as pages are added/removed: an API Reference endpoint
   *  gets the same POST badge the sidebar shows it, an in-page anchor
   *  (fragment set) gets "#", and a plain page match gets a document
   *  icon the same distinction real docs search UIs (Stripe, Claude
   *  Code's own) draw between "found the page" and "found a section
   *  inside it". */
  iconType(entry: DocsSearchResult): 'endpoint' | 'anchor' | 'page' {
    if (entry.fragment) return 'anchor';
    return entry.page.startsWith('api-reference/') ? 'endpoint' : 'page';
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const entry = this.results[this.activeIndex];
      if (entry) this.select(entry);
    }
  }

  select(entry: DocsSearchResult): void {
    const route = entry.page ? `/docs/${entry.page}` : '/docs';
    this.router.navigate([route], entry.fragment ? { fragment: entry.fragment } : {});
    this.modalRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
